import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getProjects, archiveProject, uploadProjectWithData, deleteProject, resetProjectData } from '@/DB/CRUD';
import { Project } from '@/lib/constants';
import { MoreVertical, Download, Trash2, RotateCcw, LogOut } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import * as XLSX from 'xlsx';
import { stripLotSuffix } from '@/lib/utils';


function ProjectsOverview() {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [createdProjectName, setCreatedProjectName] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExportBeforeDelete, setShowExportBeforeDelete] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<{ id: string; name: string } | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showExportBeforeReset, setShowExportBeforeReset] = useState(false);
  const [projectToReset, setProjectToReset] = useState<{ id: string; name: string } | null>(null);
  const [resetLocation, setResetLocation] = useState<'paint' | 'load' | 'both'>('both');

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDownload = async (e: React.MouseEvent, job_id: string) => {
    e.stopPropagation();
    setOpenMenuId(null);
    try {
      await archiveProject(job_id);
    } catch (err) {
      console.error('Failed to download project:', err);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, job_id: string, job_name: string) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setProjectToDelete({ id: job_id, name: job_name });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;
    
    // Close first confirmation and show export option
    setShowDeleteConfirm(false);
    setShowExportBeforeDelete(true);
  };

  const handleExportAndDelete = async () => {
    if (!projectToDelete) return;
    
    try {
      // Export the project first
      await archiveProject(projectToDelete.id);
      // Then delete it
      await deleteProjectAndRefresh();
    } catch (err) {
      console.error('Export failed:', err);
      setShowExportBeforeDelete(false);
      setErrorMessage('Failed to export project. Deletion cancelled.');
      setShowErrorPopup(true);
      setProjectToDelete(null);
    }
  };

  const handleDeleteWithoutExport = async () => {
    await deleteProjectAndRefresh();
  };

  const deleteProjectAndRefresh = async () => {
    if (!projectToDelete) return;
    
    setShowExportBeforeDelete(false);
    
    try {
      await deleteProject(projectToDelete.id);
    } catch (err) {
      console.error('Delete API error (may still have succeeded):', err);
    }
    
    // Always refresh and show success since the project is actually deleted
    try {
      const data = await getProjects();
      setProjects(data);
      setCreatedProjectName(projectToDelete.name);
      setShowSuccessPopup(true);
      setProjectToDelete(null);
    } catch (err) {
      console.error('Failed to refresh projects:', err);
      setErrorMessage('Project may have been deleted, but failed to refresh list. Please refresh the page.');
      setShowErrorPopup(true);
      setProjectToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setShowExportBeforeDelete(false);
    setProjectToDelete(null);
  };

  const handleResetClick = (e: React.MouseEvent, job_id: string, job_name: string) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setProjectToReset({ id: job_id, name: job_name });
    setShowResetConfirm(true);
  };

  const confirmReset = async () => {
    if (!projectToReset) return;
    
    // Close first confirmation and show export option
    setShowResetConfirm(false);
    setShowExportBeforeReset(true);
  };

  const handleExportAndReset = async () => {
    if (!projectToReset) return;
    
    try {
      // Export the project first
      await archiveProject(projectToReset.id);
      // Then reset it
      await resetProjectAndRefresh();
    } catch (err) {
      console.error('Export failed:', err);
      setShowExportBeforeReset(false);
      setErrorMessage('Failed to export project. Reset cancelled.');
      setShowErrorPopup(true);
      setProjectToReset(null);
    }
  };

  const handleResetWithoutExport = async () => {
    await resetProjectAndRefresh();
  };

  const resetProjectAndRefresh = async () => {
    if (!projectToReset) return;
    
    setShowExportBeforeReset(false);
    
    try {
      await resetProjectData(projectToReset.id, resetLocation);
      
      const data = await getProjects();
      setProjects(data);
      setCreatedProjectName(projectToReset.name);
      setShowSuccessPopup(true);
      setProjectToReset(null);
      setResetLocation('both');
    } catch (err) {
      console.error('Failed to reset project:', err);
      setErrorMessage('Failed to reset project. Please try again.');
      setShowErrorPopup(true);
      setProjectToReset(null);
      setResetLocation('both');
    }
  };

  const cancelReset = () => {
    setShowResetConfirm(false);
    setShowExportBeforeReset(false);
    setProjectToReset(null);
    setResetLocation('both');
  };

  const handleReset = async (e: React.MouseEvent, job_id: string, job_name: string) => {
    handleResetClick(e, job_id, job_name);
  };

  const toggleMenu = (e: React.MouseEvent, job_id: string) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === job_id ? null : job_id);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploading(true);
      try {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        // Skip first row (headers) and process data
        const dataRows = jsonData.slice(1).filter(row => row.length > 0);

        if (dataRows.length === 0) {
          console.error('No data found in Excel file');
          setIsUploading(false);
          return;
        }

        // Group rows by composite key: (job_id, lot_no) — column index 2 and 10
        // This means each unique (job_id, lot_no) pair becomes its own project.
        // Rows with null/empty lot_no are grouped under a '-unlotted' suffix.
        const lotGroups = new Map<string, any[][]>();

        for (const row of dataRows) {
          const jobRaw = row[2];
          const lotRaw = row[10];
          const lotSuffix =
            lotRaw != null && String(lotRaw).trim() !== ''
              ? String(lotRaw).trim()
              : 'unlotted';
          const compositeKey = `${jobRaw}-${lotSuffix}`;

          if (!lotGroups.has(compositeKey)) {
            lotGroups.set(compositeKey, []);
          }
          lotGroups.get(compositeKey)!.push(row);
        }

        const lotKeys = Array.from(lotGroups.keys());
        const lotLabels: string[] = [];

        // Process each group sequentially — stop on first error
        for (const mapKey of lotKeys) {
          const groupRows = lotGroups.get(mapKey)!;
          const firstRow = groupRows[0];
          const lotRaw = firstRow[10];
          const lot_number =
            lotRaw != null && String(lotRaw).trim() !== ''
              ? String(lotRaw).trim()
              : null;
          const job_id = mapKey; // composite key: `${row[2]}-${lot_suffix}`
          const job_name = firstRow[24]; // Column #25 (index 24)

          lotLabels.push(lot_number ?? 'unlotted');

          console.log(`Processing lot: ${lot_number}, job_id: ${job_id}, rows: ${groupRows.length}`);

          // Build data rows for this lot group — project_id links to this group's project
          const groupData = groupRows.map(row => ({
            serial: row[0],       // Column A (index 0) - serial/barcode
            project_id: job_id,   // Links to this lot's project
            stock: row[12],       // Column M (index 12) - stock description
            mark: row[5] != null ? String(row[5]) : null,  // Column F (index 5) - piece mark
            length: (() => {                                 // Column N (index 13) - length
              if (row[13] == null) return null;
              const s = String(row[13]);
              // If value has a decimal point, strip decimals and increment integer part by 1
              if (s.includes('.')) {
                return String(parseInt(s, 10) + 1);
              }
              return s;
            })(),
            weight_t: row[240] != null && !isNaN(Number(row[240]))  // Column 241 (index 240) - Weight (T)
              ? Number(row[240])
              : null
          }));

          await uploadProjectWithData(job_id, job_name, 0, groupRows.length, 'pending', lot_number, groupData);
        }

        // Refresh projects list
        const data = await getProjects();
        setProjects(data);

        // Show success popup with summary of all uploaded lots
        const lotCount = lotKeys.length;
        const summaryMsg = `Uploaded ${lotCount} project${lotCount !== 1 ? 's' : ''} (lots: ${lotLabels.join(', ')})`;
        setCreatedProjectName(summaryMsg);
        setIsUploading(false);
        setShowSuccessPopup(true);

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (err) {
        console.error('Failed to process Excel file:', err);
        console.log('Error type:', typeof err);
        console.log('Error object:', JSON.stringify(err, null, 2));

        let errorMsg = '';
        if (err instanceof Error) {
          errorMsg = err.message;
        } else if (typeof err === 'string') {
          errorMsg = err;
        } else {
          errorMsg = String(err);
        }

        console.log('Error message:', errorMsg);

        setIsUploading(false);

        if (errorMsg.includes('Conflict') || errorMsg.includes('409')) {
          setErrorMessage('This project already exists. Please delete the existing project or use a different file.');
        } else {
          setErrorMessage('Failed to upload project. Please ensure the project has not already been uploaded.');
        }

        setShowErrorPopup(true);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  useEffect(() => {
    async function fetchProjects() {
      try {
        setLoading(true);
        const data = await getProjects();
        setProjects(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch projects');
      } finally {
        setLoading(false);
      }
    }

    fetchProjects();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'text-green-600';
      case 'in progress':
      case 'in_progress':
        return 'text-blue-600';
      case 'pending':
        return 'text-yellow-600';
      case 'checked':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const getProgressBarColor = (status: string, progress: number) => {
    if (status.toLowerCase() === 'completed' || progress === 100) return 'bg-green-500';
    if (status.toLowerCase() === 'in progress' || status.toLowerCase() === 'in_progress') {
      if (progress > 50) return 'bg-green-500';
      return 'bg-orange-500';
    }
    return 'bg-gray-400';
  };

  const calculateProgress = (scanned: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((scanned / total) * 100);
  };

  const getDisplayStatus = (project: Project) => {
    const paintCount = project.paint_scan_count ?? 0;
    const loadCount = project.load_scan_count ?? 0;
    if (loadCount >= project.scan_total && project.scan_total > 0) return 'Complete';
    if (paintCount > 0 || loadCount > 0) return 'In Progress';
    return 'Pending';
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={6} className="text-center text-gray-500 py-8">
            Loading projects...
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={6} className="text-center text-gray-500 py-8">
            {error}
          </TableCell>
        </TableRow>
      );
    }

    if (projects.length === 0) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={6} className="text-center text-gray-500 py-8">
            No projects available
          </TableCell>
        </TableRow>
      );
    }

    return projects.map((project, index) => {
      const progress = calculateProgress(project.scan_count, project.scan_total);
      const displayStatus = getDisplayStatus(project);
      return (
        <TableRow
          key={project.job_id}
          onClick={() => navigate(`/project/${project.job_id}`)}
          className="cursor-pointer hover:bg-gray-200"
        >
          <TableCell className="font-semibold">
            {stripLotSuffix(project.job_name, project.lot_number)}
          </TableCell>
          <TableCell className="text-gray-600">
            {project.lot_number ?? <span className="italic text-gray-400">Unlotted</span>}
          </TableCell>
          <TableCell className={getStatusColor(displayStatus)}>
            {formatStatus(displayStatus)}
          </TableCell>
          <TableCell>
            <div className="flex flex-col gap-1 min-w-[160px]">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-10">Paint</span>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    role="progressbar"
                    aria-valuenow={calculateProgress(project.paint_scan_count ?? 0, project.scan_total)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    className={`h-2 rounded-full ${getProgressBarColor(displayStatus, calculateProgress(project.paint_scan_count ?? 0, project.scan_total))}`}
                    style={{ width: `${calculateProgress(project.paint_scan_count ?? 0, project.scan_total)}%` }}
                  ></div>
                </div>
                <span className="text-xs text-gray-600 w-8">{calculateProgress(project.paint_scan_count ?? 0, project.scan_total)}%</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-10">Load</span>
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    role="progressbar"
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    className={`h-2 rounded-full ${getProgressBarColor(displayStatus, progress)}`}
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <span className="text-xs text-gray-600 w-8">{progress}%</span>
              </div>
            </div>
          </TableCell>
          <TableCell>
            <div className="text-sm">
              <div>Paint: {project.paint_scan_count ?? 0}/{project.scan_total}</div>
              <div>Load: {project.load_scan_count ?? 0}/{project.scan_total}</div>
            </div>
          </TableCell>
          <TableCell className="relative overflow-visible">
            <div ref={openMenuId === project.job_id ? menuRef : null} className="relative">
              <button
                onClick={(e) => toggleMenu(e, project.job_id)}
                className="p-1 rounded hover:bg-gray-300 transition-colors"
                id={`menu-button-${project.job_id}`}
              >
                <MoreVertical className="h-5 w-5 text-gray-600" />
              </button>
              {openMenuId === project.job_id && (
                <div 
                  className="fixed bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[150px]"
                  style={{
                    top: `${document.getElementById(`menu-button-${project.job_id}`)?.getBoundingClientRect().bottom ?? 0}px`,
                    right: `${window.innerWidth - (document.getElementById(`menu-button-${project.job_id}`)?.getBoundingClientRect().right ?? 0)}px`
                  }}
                >
                  <button
                    onClick={(e) => handleDownload(e, project.job_id)}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Export Project
                  </button>
                  {isAdmin && (
                    <>
                    <button
                      onClick={(e) => handleReset(e, project.job_id, project.job_name)}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-orange-600"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Reset Project
                    </button>
                    <button
                      onClick={(e) => handleDeleteClick(e, project.job_id, project.job_name)}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Project
                    </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {isUploading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-xl">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#27ae60]"></div>
              <p className="mt-4 text-gray-700 font-semibold">Uploading project...</p>
            </div>
          </div>
        </div>
      )}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm">
            <h3 className="text-xl font-bold text-green-600 mb-2">Success!</h3>
            <p className="text-gray-700 mb-4">
              {projectToDelete
                ? `Project "${createdProjectName}" deleted successfully.`
                : createdProjectName}
            </p>
            <Button 
              onClick={() => setShowSuccessPopup(false)}
              className="bg-[#27ae60] hover:bg-[#229954] text-white w-full"
            >
              OK
            </Button>
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm">
            <h3 className="text-xl font-bold text-red-600 mb-2">Confirm Delete</h3>
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete project "{projectToDelete?.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-2">
              <Button 
                onClick={cancelDelete}
                className="bg-gray-500 hover:bg-gray-600 text-white flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white flex-1"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      {showExportBeforeDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm">
            <h3 className="text-xl font-bold text-orange-600 mb-2">Export Before Delete?</h3>
            <p className="text-gray-700 mb-4">
              Would you like to export project "{projectToDelete?.name}" before deleting it?
            </p>
            <div className="flex flex-col gap-2">
              <Button 
                onClick={handleExportAndDelete}
                className="bg-[#27ae60] hover:bg-[#229954] text-white w-full"
              >
                Yes, Export & Delete
              </Button>
              <Button 
                onClick={handleDeleteWithoutExport}
                className="bg-red-600 hover:bg-red-700 text-white w-full"
              >
                No, Just Delete
              </Button>
              <Button 
                onClick={cancelDelete}
                className="bg-gray-500 hover:bg-gray-600 text-white w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full">
            <h3 className="text-xl font-bold text-orange-600 mb-2">Confirm Reset</h3>
            <p className="text-gray-700 mb-4">
              Are you sure you want to reset project "{projectToReset?.name}"? All scan data for the selected location will be cleared.
            </p>
            <div className="flex flex-col gap-1 mb-4">
              <label className="text-sm font-medium text-gray-600">Reset Location</label>
              <select
                value={resetLocation}
                onChange={e => setResetLocation(e.target.value as 'paint' | 'load' | 'both')}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                <option value="both">Both (Paint &amp; Load)</option>
                <option value="paint">Paint only</option>
                <option value="load">Load only</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={cancelReset}
                className="bg-gray-500 hover:bg-gray-600 text-white flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmReset}
                className="bg-orange-600 hover:bg-orange-700 text-white flex-1"
              >
                Reset
              </Button>
            </div>
          </div>
        </div>
      )}
      {showExportBeforeReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm">
            <h3 className="text-xl font-bold text-orange-600 mb-2">Export Before Reset?</h3>
            <p className="text-gray-700 mb-4">
              Would you like to export project "{projectToReset?.name}" before resetting it?
            </p>
            <div className="flex flex-col gap-2">
              <Button 
                onClick={handleExportAndReset}
                className="bg-[#27ae60] hover:bg-[#229954] text-white w-full"
              >
                Yes, Export & Reset
              </Button>
              <Button 
                onClick={handleResetWithoutExport}
                className="bg-orange-600 hover:bg-orange-700 text-white w-full"
              >
                No, Just Reset
              </Button>
              <Button 
                onClick={cancelReset}
                className="bg-gray-500 hover:bg-gray-600 text-white w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {showErrorPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm">
            <h3 className="text-xl font-bold text-red-600 mb-2">Error</h3>
            <p className="text-gray-700 mb-4">{errorMessage}</p>
            <Button 
              onClick={() => setShowErrorPopup(false)}
              className="bg-red-600 hover:bg-red-700 text-white w-full"
            >
              OK
            </Button>
          </div>
        </div>
      )}
      <Card className="rounded-t-lg shadow-md m-5 border-0 overflow-visible">
        <CardHeader className="bg-[#2c3e50] text-white py-10 px-5 flex flex-row justify-between items-center rounded-t-lg">
          <CardTitle className="text-4xl font-bold font-['Arial'] text-white m-0">
            O'Dwyer Steel - Order Verification System
          </CardTitle>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-300">
              {user?.username}{' '}
              <span className="bg-[#27ae60] text-white text-xs px-2 py-0.5 rounded-full uppercase">
                {user?.role}
              </span>
            </span>
            {isAdmin && (
              <Button variant="default" className="bg-[#27ae60] hover:bg-[#229954] text-white" onClick={handleUploadClick}>
                Upload Excel
              </Button>
            )}
            {isAdmin && (
              <Button variant="default" className="bg-[#27ae60] hover:bg-[#229954] text-white" onClick={() => navigate('/users')}>
                Manage Users
              </Button>
            )}
            <Button variant="secondary" className="bg-[#e74c3c] hover:bg-[#c0392b] text-white" onClick={logout}>
              <LogOut className="h-4 w-4 mr-1" /> Sign Out
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
          />
        </CardHeader>
        <CardContent className="p-5 pb-32 overflow-visible">
          <CardTitle className="text-2xl font-bold mt-5 font-['Arial'] mb-4">
            Projects Overview
          </CardTitle>
          
          <Table className="overflow-visible">
            <TableHeader>
              <TableRow className="hover:bg-[#34495e]">
                <TableHead className="bg-[#34495e] text-white">Project Name</TableHead>
                <TableHead className="bg-[#34495e] text-white">Lot Number</TableHead>
                <TableHead className="bg-[#34495e] text-white">Status</TableHead>
                <TableHead className="bg-[#34495e] text-white">Progress</TableHead>
                <TableHead className="bg-[#34495e] text-white">Items</TableHead>
                <TableHead className="bg-[#34495e] text-white w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderTableContent()}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default ProjectsOverview;
